import { QueryStringInput } from '@hello-aria/query-string'
import { startOfDay } from 'date-fns'
import { isEmpty, isNil } from 'lodash'
import {
    CreateInvoice,
    CreateInvoiceGateway,
    Invoice,
    InvoiceGateway,
    LinkReceivable,
    LinkReceivablePayload,
    UnlinkReceivable,
    UnlinkReceivablePayload,
    UpdateInvoice,
    UpdateInvoiceGateway
} from '../models/invoiceModels'
import { UploadFileGateway } from '../models/uploadModels'
import { dateToISOStringWithTimeZoneOffset } from '../utils/dateUtils'
import { batchFetch, resolveApiUrl } from '../utils/requestsUtils'
import { RequestResponse, requestService } from './requestServices'
import { downloadAttachments, uploadFileService } from './uploadServices'
import { removeEmailWhitespace } from '../utils/inputUtils'
import { trackEvent } from '../utils/analyticsUtils'
import { unpaidLoansStatus } from '../models/loanModels'

interface GetInvoices {
    companyId: string
    hasAttachments: boolean
    query?: QueryStringInput
    signal?: AbortSignal
}

export const getInvoicesService = batchFetch(
    async (params: GetInvoices): Promise<RequestResponse<Invoice[]> | undefined> => {
        const url = resolveApiUrl('invoice.getAll', {
            query: {
                ...params.query,
                params: {
                    ...params.query?.params,
                    include: 'loans'
                }
            },
            variables: { companyId: params.companyId }
        })

        const response = await requestService<InvoiceGateway[]>(
            url,
            params.signal
                ? {
                      signal: params.signal
                  }
                : undefined
        )

        if (response?.body) {
            const mappedInvoices = await Promise.all(
                response?.body?.map((invoice) => mapInvoiceService(invoice, params.hasAttachments))
            )
            const sanitizedMappedInvoices = mappedInvoices.filter((invoice): invoice is Invoice => !isNil(invoice))
            return { ...response, body: sanitizedMappedInvoices }
        }
    }
)

interface GetInvoiceById {
    companyId: string
    invoiceId: string | undefined
    hasAttachments: boolean
}

export const getInvoiceByIdService = async (params: GetInvoiceById): Promise<RequestResponse<Invoice> | undefined> => {
    if (isNil(params.invoiceId)) return undefined

    const { companyId, invoiceId, hasAttachments } = params
    const url = resolveApiUrl('invoice.get', { variables: { companyId, invoiceId } })
    const response = await requestService<InvoiceGateway>(url + '?include=loans')
    const mappedInvoice = await mapInvoiceService(response?.body, hasAttachments)

    return { ...response, body: mappedInvoice }
}

export const createInvoiceService = async (
    companyId: string,
    payload: CreateInvoice
): Promise<RequestResponse<Invoice> | undefined> => {
    const url = resolveApiUrl('invoice.create', { variables: { companyId } })

    const files = await uploadFileService(payload.attachments, companyId)

    const formattedData = mapCreateInvoiceService(payload, files)
    const response = await requestService<InvoiceGateway>(url, {
        method: 'POST',
        data: formattedData
    })
    const mappedInvoice = await mapInvoiceService(response?.body)

    trackEvent('Create_Invoice')

    return { ...response, body: mappedInvoice }
}

export const updateInvoiceService = async (
    companyId: string,
    payload: UpdateInvoice
): Promise<RequestResponse<Invoice> | undefined> => {
    const { invoiceId } = payload
    const url = resolveApiUrl('invoice.update', { variables: { companyId, invoiceId } })

    const files = await uploadFileService(payload.attachments, companyId)

    const formattedData = mapUpdateInvoiceService(payload, files)
    const response = await requestService<InvoiceGateway>(url, { method: 'PATCH', data: formattedData })

    const mappedInvoice = await mapInvoiceService(response?.body, false)

    // Tracking event for invoice update
    trackEvent('Update_Invoice')

    return { ...response, body: mappedInvoice }
}

export const unlinkReceivableFromInvoiceService = async (
    companyId: string,
    payload: UnlinkReceivable
): Promise<RequestResponse<Invoice> | undefined> => {
    const { invoiceId } = payload
    const url = resolveApiUrl('invoice.update', { variables: { companyId, invoiceId } })

    const formattedData: UnlinkReceivablePayload = {
        receivableIds: payload.currentReceivableIds.filter((id) => id !== payload.receivableIdToUnlink)
    }

    const response = await requestService<InvoiceGateway>(url, { method: 'PATCH', data: formattedData })

    const mappedInvoice = await mapInvoiceService(response?.body, false)

    trackEvent('Update_Invoice')

    return { ...response, body: mappedInvoice }
}

export const linkReceivableToInvoiceService = async (
    companyId: string,
    payload: LinkReceivable
): Promise<RequestResponse<Invoice> | undefined> => {
    const { invoiceId } = payload
    const url = resolveApiUrl('invoice.update', { variables: { companyId, invoiceId } })
    const formattedData: LinkReceivablePayload = {
        receivableIds: [...payload.currentReceivableIds, ...(payload.receivableIdsToLink ?? [])]
    }
    const response = await requestService<InvoiceGateway>(url, {
        method: 'PATCH',
        data: formattedData
    })
    const mappedInvoice = await mapInvoiceService(response?.body, false)

    trackEvent('Update_Invoice')

    return { ...response, body: mappedInvoice }
}

export const mapInvoiceService = async (
    data: InvoiceGateway | undefined,
    hasAttachments = true
): Promise<Invoice | undefined> => {
    try {
        if (isNil(data)) return undefined

        const attachmentsCount = data?.attachments?.length ?? 0

        return {
            ...data,
            isEditable: data?.loans?.every((loan) => unpaidLoansStatus.includes(loan.status)) ?? true,
            attachments: hasAttachments ? await downloadAttachments(data.attachments) : undefined,
            attachmentCount: attachmentsCount
        }
    } catch (error) {
        if (error instanceof Error) {
            console.error(error.message)
        }

        console.error(error)
    }
}

const mapCreateInvoiceService = (
    payload: CreateInvoice,
    files?: UploadFileGateway[] | undefined
): CreateInvoiceGateway => {
    return {
        debtorIdentifier: payload.debtorIdentifier,
        debtorName: payload.debtorName,
        invoiceDate: dateToISOStringWithTimeZoneOffset(startOfDay(new Date(payload.invoiceDate))),
        invoiceOwner: payload.invoiceOwner,
        invoiceNumber: payload.invoiceNumber,
        repaymentPeriod: payload.repaymentPeriod,
        paymentTermType: payload.paymentTermType,
        totalAmountExcludingTaxes: Number(payload.totalAmountExcludingTaxes) || undefined,
        totalAmountIncludingTaxes: Number(payload.totalAmountIncludingTaxes),
        currency: payload.currency,
        contacts: payload.contacts.map((contact) => removeEmailWhitespace(contact)),
        attachments: !isEmpty(files) && !isNil(files) ? files.map((file) => ({ url: file.url })) : undefined
    }
}

const mapUpdateInvoiceService = (
    payload: UpdateInvoice,
    files?: UploadFileGateway[] | undefined
): UpdateInvoiceGateway => {
    const debtorIdentifier = payload.debtorIdentifier

    return {
        paymentTermType: payload.paymentTermType,
        totalAmountExcludingTaxes: Number(payload.totalAmountExcludingTaxes),
        totalAmountIncludingTaxes: Number(payload.totalAmountIncludingTaxes),
        debtorName: payload.debtorName,
        debtorIdentifier,
        debtorId: payload.debtorId,
        invoiceDate: payload.invoiceDate ? dateToISOStringWithTimeZoneOffset(new Date(payload.invoiceDate)) : undefined,
        invoiceNumber: payload.invoiceNumber,
        repaymentPeriod: payload.repaymentPeriod,
        currency: payload.currency,
        contacts: payload.contacts?.map((contact) => removeEmailWhitespace(contact)) ?? [],
        attachments: !isEmpty(files) && !isNil(files) ? files.map((file) => ({ url: file.url })) : [],
        invoiceOwner: payload.invoiceOwner
    }
}

export const fetchFileFromInvoiceService = async (url: string): Promise<File | undefined> => {
    try {
        const response = await requestService<Blob>(url, {
            headers: { 'Access-Control-Expose-Headers': 'Content-Disposition' },
            responseType: 'blob'
        })

        const header = response?.headers?.['content-disposition']
        const parts = header?.split(';')
        const filename = parts?.[1]?.split('=')[1]

        const blob = response?.body as Blob
        const file = new File([blob], filename ?? '', { type: blob.type })

        return file
    } catch (error) {
        console.error('Error fetching files:', error)
    }
}
